import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

// Футер-блок SDK (2 вкладки: Меню + Топ запити). Вынесено из SdkView в отдельный раздел.
export default function SdkFooterView() {
  const { proj, activeId, apiFetch, dark, t, card, inp, btnP, btnG, btnSm, flash } = useApp();
  // Великі міста України (2026) — дефолт для поля «Міста», за мовою проєкту (UA/RU написання).
  const UA_CITIES = ["Київ","Харків","Одеса","Дніпро","Запоріжжя","Львів","Кривий Ріг","Миколаїв","Вінниця","Полтава","Чернігів","Черкаси","Житомир","Суми","Хмельницький","Чернівці","Рівне","Івано-Франківськ","Тернопіль","Луцьк","Кропивницький","Ужгород"];
  const RU_CITIES = ["Киев","Харьков","Одесса","Днепр","Запорожье","Львов","Кривой Рог","Николаев","Винница","Полтава","Чернигов","Черкассы","Житомир","Сумы","Хмельницкий","Черновцы","Ровно","Ивано-Франковск","Тернополь","Луцк","Кропивницкий","Ужгород"];
  const defaultCities = () => String(proj?.lang || "").toUpperCase() === "RU" ? RU_CITIES : UA_CITIES;

  const [footerCfg, setFooterCfg] = useState({ enabled: false, selector: "footer", position: "before", bgColor: "", textColor: "", accentColor: "", menu: [], queries: { pagesGenerated: 0, lastGeneratedAt: null } });
  const [footerSaving, setFooterSaving] = useState(false);
  const [footerRegen, setFooterRegen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [structurePages, setStructurePages] = useState([]); // [{url, name, depth}]
  const [clusters, setClusters] = useState([]); // [{url, hfKeyword}]
  const [menuFilter, setMenuFilter] = useState("");
  const [dragCatUrl, setDragCatUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState({ items: [], collectedAt: null, pagesCrawled: 0 });
  const [collectingCards, setCollectingCards] = useState(false);
  const [footerFilters, setFooterFilters] = useState({ items: [], importedAt: null });
  const [importingFilters, setImportingFilters] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    Promise.all([
      apiFetch(`/api/projects/${activeId}/footer/config`).then(r => r.json()).then(d => { if (d) setFooterCfg(prev => ({ ...prev, ...d, cities: (Array.isArray(d.cities) && d.cities.length) ? d.cities : defaultCities() })); }).catch(() => {}),
      apiFetch(`/api/projects/${activeId}/footer/structure`).then(r => r.json()).then(d => { if (d?.pages) setStructurePages(d.pages); }).catch(() => {}),
      apiFetch(`/api/projects/${activeId}/clusters`).then(r => r.json()).then(d => { if (Array.isArray(d)) setClusters(d); }).catch(() => {}),
      apiFetch(`/api/projects/${activeId}/footer/cards`).then(r => r.json()).then(d => { if (d) setCards(d); }).catch(() => {}),
      apiFetch(`/api/projects/${activeId}/footer/filters`).then(r => r.json()).then(d => { if (d) setFooterFilters(d); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  const collectCards = async () => {
    setCollectingCards(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/collect-cards`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        const fresh = await apiFetch(`/api/projects/${activeId}/footer/cards`).then(x => x.json());
        setCards(fresh);
        flash(`Зібрано карток: ${d.count} (проскановано сторінок: ${d.pagesCrawled})`);
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setCollectingCards(false);
  };

  const [importingCards, setImportingCards] = useState(false);
  const importCards = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportingCards(true);
    try {
      const text = await file.text();
      const rows = text.split(/\r?\n/).map((ln) => {
        const parts = ln.split(/\t|;|,/).map((x) => x.trim()).filter(Boolean);
        let url = "", title = "";
        for (const p of parts) { if (/^https?:\/\//i.test(p)) { if (!url) url = p; } else if (!title) title = p; }
        return { title, url };
      }).filter((r) => r.url);
      if (!rows.length) { flash("У файлі не знайдено рядків з URL (http/https)."); e.target.value = ""; setImportingCards(false); return; }
      const r = await apiFetch(`/api/projects/${activeId}/footer/import-cards`, { method: "POST", body: JSON.stringify({ items: rows }) });
      const d = await r.json();
      if (r.ok) { const fresh = await apiFetch(`/api/projects/${activeId}/footer/cards`).then((x) => x.json()); setCards(fresh); flash(`Завантажено карток із файлу: ${d.count}`); }
      else flash(d.error || "Помилка імпорту.");
    } catch (err) { flash("Помилка читання файлу."); }
    e.target.value = ""; setImportingCards(false);
  };

  const [rewritingCards, setRewritingCards] = useState(false);
  const rewriteCards = async () => {
    if (rewritingCards) return;
    if (!window.confirm("Переписати назви карток під частотний ключ через OpenAI? На початку кожної назви стане головний (частотний) ключ сторінки. Оригінали зберігаються.")) return;
    setRewritingCards(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/rewrite-cards`, { method: "POST" });
      const d = await r.json();
      if (r.ok) {
        const fresh = await apiFetch(`/api/projects/${activeId}/footer/cards`).then(x => x.json());
        setCards(fresh);
        flash(`✨ Переписано: ${d.rewritten}${d.unchanged ? ` · без змін: ${d.unchanged}` : ""} (оброблено ${d.processed} з ${d.total})`);
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setRewritingCards(false);
  };

  const [cardEdits, setCardEdits] = useState({}); // url -> new title
  const [cardSearch, setCardSearch] = useState("");
  const [cardPage, setCardPage] = useState(0);
  const [savingCards, setSavingCards] = useState(false);
  const saveCards = async () => {
    const updates = Object.entries(cardEdits).filter(([, v]) => v != null && String(v).trim() !== "").map(([url, title]) => ({ url, title }));
    if (!updates.length) { flash("Немає змін"); return; }
    setSavingCards(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/update-cards`, { method: "POST", body: JSON.stringify({ updates }) });
      const d = await r.json();
      if (r.ok) {
        const fresh = await apiFetch(`/api/projects/${activeId}/footer/cards`).then(x => x.json());
        setCards(fresh); setCardEdits({});
        flash(`✓ Збережено назв: ${d.updated}`);
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setSavingCards(false);
  };

  // Топ-фільтри: експорт CSV-шаблону (blob-download через apiFetch, бо потрібен auth-заголовок).
  const exportFiltersCsv = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/filters.csv`);
      if (!r.ok) { flash("Помилка експорту"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `footer-filters-${activeId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const importFiltersCsv = async (file) => {
    if (!file) return;
    setImportingFilters(true);
    try {
      const csv = await file.text();
      const r = await apiFetch(`/api/projects/${activeId}/footer/filters/import`, { method: "POST", body: JSON.stringify({ csv }) });
      const d = await r.json();
      if (r.ok) {
        const fresh = await apiFetch(`/api/projects/${activeId}/footer/filters`).then(x => x.json());
        setFooterFilters(fresh);
        flash(`✓ Імпортовано значень: ${d.count}${d.global ? ` · глобальних: ${d.global}` : ""}${d.pages ? ` · на сторінках: ${d.pages}` : ""}`);
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setImportingFilters(false);
  };

  // path → ВЧ-ключ (hf_keyword) з кластерів, для підстановки в анкори топ-меню
  const hfByPath = useMemo(() => {
    const norm = (u) => { try { return (new URL(u, "https://x").pathname || "/").replace(/\/+$/, "").toLowerCase() || "/"; } catch { return String(u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/+$/, "").toLowerCase() || "/"; } };
    const map = new Map();
    for (const c of clusters) { if (c.url && c.hfKeyword) map.set(norm(c.url), c.hfKeyword); }
    return map;
  }, [clusters]);

  const normPath = (u) => { try { return (new URL(u, "https://x").pathname || "/").replace(/\/+$/, "").toLowerCase() || "/"; } catch { return String(u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/+$/, "").toLowerCase() || "/"; } };

  const applyHfToMenu = () => {
    const menu = footerCfg.menu || [];
    if (!menu.length) { flash("Спершу оберіть пункти меню"); return; }
    let n = 0;
    const next = menu.map(m => {
      const hf = hfByPath.get(normPath(m.url));
      if (hf && hf !== m.anchor) { n++; return { ...m, anchor: hf }; }
      return m;
    });
    saveFooterCfg({ ...footerCfg, menu: next });
    flash(n ? `Підтягнуто ВЧ-ключі: ${n} з ${menu.length}` : "Для обраних сторінок ВЧ-ключів у кластерах не знайдено");
  };

  // Вивантаження сформованого топ-меню (обрані пункти + анкори/ключі) у CSV-таблицю.
  // Порядок і рівень вкладеності — зі структури сайту; пункти поза структурою додаються в кінець.
  const exportMenuCsv = () => {
    const menu = footerCfg.menu || [];
    if (!menu.length) { flash("Спершу оберіть пункти меню"); return; }
    const menuMap = new Map(menu.map(m => [m.url, m.anchor]));
    const rows = [];
    const seen = new Set();
    structurePages.forEach(p => {
      if (!menuMap.has(p.url)) return;
      seen.add(p.url);
      rows.push({ anchor: menuMap.get(p.url) || p.name, url: p.url, depth: p.depth || 0, hf: hfByPath.get(normPath(p.url)) || "" });
    });
    menu.forEach(m => { if (!seen.has(m.url)) rows.push({ anchor: m.anchor || "", url: m.url, depth: 0, hf: hfByPath.get(normPath(m.url)) || "" }); });

    const esc = (v) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const header = ["№", "Пункт меню (анкор-ключ)", "URL", "Шлях", "ВЧ-ключ (hf)", "Рівень"];
    const lines = [header.map(esc).join(",")];
    rows.forEach((r, i) => {
      lines.push([i + 1, r.anchor, r.url, (r.url || "").replace(/^https?:\/\/[^/]+/, "") || "/", r.hf, r.depth].map(esc).join(","));
    });
    const csv = "﻿" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `top-menu-${activeId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    flash(`Вивантажено ${rows.length} пунктів топ-меню`);
  };

  // Топ-категорії футера (верхній рівень дерева) у поточному порядку — для UI перетягування.
  const catRoots = useMemo(() => {
    const roots = buildPrunedMenuTree(structurePages, new Set((footerCfg.menu || []).map(m => m.url)));
    return orderRoots(roots, footerCfg.catOrder);
  }, [structurePages, footerCfg.menu, footerCfg.catOrder]);

  const moveCat = (fromUrl, toUrl) => {
    if (!fromUrl || fromUrl === toUrl) return;
    const order = catRoots.map(r => r.url);
    const from = order.indexOf(fromUrl), to = order.indexOf(toUrl);
    if (from < 0 || to < 0) return;
    order.splice(to, 0, order.splice(from, 1)[0]);
    saveFooterCfg({ ...footerCfg, catOrder: order });
  };
  const moveCatBy = (url, dir) => {
    const order = catRoots.map(r => r.url);
    const i = order.indexOf(url), j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    saveFooterCfg({ ...footerCfg, catOrder: order });
  };

  const saveFooterCfg = async (next) => {
    setFooterSaving(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/config`, { method: "PUT", body: JSON.stringify(next) });
      const d = await r.json();
      if (d.ok) setFooterCfg(prev => ({ ...prev, ...d.footerConfig }));
    } catch (e) { flash("Помилка збереження: " + e.message); }
    setFooterSaving(false);
  };

  const publishFooter = async () => {
    setPublishing(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/publish`, { method: "POST", body: JSON.stringify(footerCfg) });
      const d = await r.json();
      if (d.ok) {
        setFooterCfg(prev => ({ ...prev, ...d.footerConfig }));
        flash(`Збережено та опубліковано (версія ${d.newVersion}). Сайти підхоплять зміни при наступному завантаженні.`);
      } else flash("Помилка публікації: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setPublishing(false);
  };

  const regenFooter = async (force) => {
    setFooterRegen(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/regenerate${force ? "?force=1" : ""}`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        flash(`Згенеровано: ${d.inserted} нових сторінок · пропущено ${d.skipped} (вже були) · pool: ${d.anchorPoolSize} анкорів × ${d.urlPoolSize} URL`);
        const cfg = await apiFetch(`/api/projects/${activeId}/footer/config`).then(x => x.json());
        setFooterCfg(prev => ({ ...prev, ...cfg }));
      } else {
        flash("Помилка: " + (d.error || "невідома"));
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setFooterRegen(false);
  };

  const [queriesList, setQueriesList] = useState(null);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [qFilter, setQFilter] = useState("");
  const loadQueries = async () => {
    setQueriesLoading(true);
    try {
      const d = await apiFetch(`/api/projects/${activeId}/footer/queries`).then(x => x.json());
      setQueriesList(Array.isArray(d.items) ? d.items : []);
    } catch (e) { flash("Помилка: " + e.message); }
    setQueriesLoading(false);
  };
  const removeQuery = async (anchor) => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/footer/queries/remove`, { method: "POST", body: JSON.stringify({ anchors: [anchor] }) });
      const d = await r.json();
      if (d.ok) { setQueriesList(prev => (prev || []).filter(x => x.anchor !== anchor)); flash(`Видалено «${anchor}» з ${d.pagesUpdated} сторінок`); }
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  if (!proj) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Футер-блок</h2>
        <span style={{ fontSize: 12, color: t.textMuted }}>{proj.domain}</span>
      </div>

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : (
        <>
          {/* Footer module — Top menu + Top queries */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 20 }}>📐</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Футер-блок ({(() => { const n = [footerCfg.tabMenu, footerCfg.tabQueries, footerCfg.tabCards].filter(v => v !== false).length + ((footerCfg.tabFilters !== false && footerFilters.items?.length) ? 1 : 0); return n + " " + (n === 1 ? "вкладка" : (n >= 2 && n <= 4 ? "вкладки" : "вкладок")); })()})</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>
                  «Меню» (однакове на всіх сторінках) + «Топ запити» (24 анкори, по одному набору на кожну сторінку, заморожено)
                </div>
              </div>
              <button onClick={publishFooter} disabled={publishing} style={{ ...btnP, fontSize: 12, opacity: publishing ? 0.6 : 1 }}>
                {publishing ? "Публікація..." : "💾 Зберегти і опублікувати"}
              </button>
              <button onClick={() => saveFooterCfg({ ...footerCfg, enabled: !footerCfg.enabled })} disabled={footerSaving} style={{
                padding: "6px 16px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                border: "none",
                background: footerCfg.enabled ? "linear-gradient(135deg, #10b981, #059669)" : (dark ? "#252535" : "#e5e7eb"),
                color: footerCfg.enabled ? "#fff" : t.textMuted,
                boxShadow: footerCfg.enabled ? "0 2px 8px rgba(16,185,129,0.3)" : "none",
              }}>{footerCfg.enabled ? "Увімкнено" : "Вимкнено"}</button>
              <span style={{ fontSize: 10.5, color: t.textMuted, marginLeft: 8 }}>{footerCfg.enabled ? "показується на сайті" : "прев\u2019ю нижче — на сайт не потрапляє, поки не увімкнете"}</span>
            </div>

            {true && (
              <>
                {/* Selector + position */}
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>Заголовок футера</label>
                    <input type="text" value={footerCfg.brandTitle ?? ""} onChange={e => setFooterCfg(prev => ({ ...prev, brandTitle: e.target.value }))}
                      onBlur={() => saveFooterCfg(footerCfg)} placeholder="Так шукають"
                      style={{ ...inp, fontSize: 13, padding: "8px 12px", width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>Підзаголовок</label>
                    <input type="text" value={footerCfg.brandSubtitle ?? ""} onChange={e => setFooterCfg(prev => ({ ...prev, brandSubtitle: e.target.value }))}
                      onBlur={() => saveFooterCfg(footerCfg)} placeholder="Внутрішня перелінковка від Spalah."
                      style={{ ...inp, fontSize: 13, padding: "8px 12px", width: "100%" }} />
                  </div>
                </div>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, color: t.text }}>
                    <input type="checkbox" checked={footerCfg.hideAttribution === true}
                      onChange={e => { const next = { ...footerCfg, hideAttribution: e.target.checked }; setFooterCfg(next); saveFooterCfg(next); }} />
                    Прибрати підпис «перелінковка Spalah від Svitlo agency»
                  </label>
                </div>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>Вкладки футера (вмик/вимк + назва)</label>
                  {[
                    { on: "tabMenu", lbl: "tabMenuLabel", ph: "Топ-меню" },
                    { on: "tabQueries", lbl: "tabQueriesLabel", ph: "Топ-запити" },
                    { on: "tabCards", lbl: "tabCardsLabel", ph: "Топ-картки" },
                    { on: "tabFilters", lbl: "tabFiltersLabel", ph: "Топ-фільтри" },
                    { on: "tabLsi", lbl: "tabLsiLabel", ph: "LSI-терміни" },
                  ].map(tb => (
                    <div key={tb.on} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, minWidth: 46 }}>
                        <input type="checkbox" checked={footerCfg[tb.on] !== false}
                          onChange={e => { const next = { ...footerCfg, [tb.on]: e.target.checked }; setFooterCfg(next); saveFooterCfg(next); }} />
                      </label>
                      <input type="text" value={footerCfg[tb.lbl] ?? ""} disabled={footerCfg[tb.on] === false}
                        onChange={e => setFooterCfg(prev => ({ ...prev, [tb.lbl]: e.target.value }))}
                        onBlur={() => saveFooterCfg(footerCfg)} placeholder={tb.ph}
                        style={{ ...inp, fontSize: 12.5, padding: "6px 10px", flex: 1, opacity: footerCfg[tb.on] === false ? 0.45 : 1 }} />
                    </div>
                  ))}
                </div>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>CSS-селектор</label>
                    <input type="text" value={footerCfg.selector || ""} onChange={e => setFooterCfg(prev => ({ ...prev, selector: e.target.value }))}
                      onBlur={() => saveFooterCfg(footerCfg)}
                      placeholder="footer"
                      style={{ ...inp, fontFamily: "monospace", fontSize: 12, padding: "8px 12px", width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>Позиція</label>
                    <select value={footerCfg.position || "before"} onChange={e => { const next = { ...footerCfg, position: e.target.value }; setFooterCfg(next); saveFooterCfg(next); }}
                      style={{ ...inp, fontSize: 12, padding: "8px 12px", width: "100%", cursor: "pointer" }}>
                      <option value="before">Перед елементом</option>
                      <option value="after">Після елемента</option>
                      <option value="prepend">На початку (як перший child)</option>
                      <option value="append">В кінці (як останній child)</option>
                    </select>
                  </div>
                  <div style={{ fontSize: 10.5, color: t.textMuted, gridColumn: "1 / -1", lineHeight: 1.5 }}>
                    Дефолт: <code>footer</code> + «Перед елементом» — блок з'явиться відразу над футером сайту. Для 24print можна також <code>body.isMacLike .wrapper</code> з «В кінці».
                  </div>
                </div>

                {/* Exclude pages by URL substring */}
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>Не показувати на сторінках, чий URL містить</label>
                  <input type="text" value={(footerCfg.excludePaths || []).join(", ")}
                    onChange={e => setFooterCfg(prev => ({ ...prev, excludePaths: e.target.value.split(/[,\n]/).map(s => s.trim()).filter(Boolean) }))}
                    onBlur={() => saveFooterCfg(footerCfg)}
                    placeholder="/ru/, /en/, /cart"
                    style={{ ...inp, fontFamily: "monospace", fontSize: 12, padding: "8px 12px", width: "100%" }} />
                  <div style={{ fontSize: 9.5, color: t.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                    Через кому. Якщо URL сторінки містить будь-який із цих підрядків — футер не рендериться (напр. <code>/ru/</code> вимкне футер на всій ру-версії). Порожньо → показувати скрізь.
                  </div>
                </div>

                {/* Color pickers */}
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Кольори</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    {[
                      { k: "bgColor",     label: "Фон блоку",     hint: "Залиште порожнім → авто (детектується по странице)", placeholder: "#1a1a1a" },
                      { k: "textColor",   label: "Колір тексту",  hint: "Текст табів та посилань", placeholder: "#ffffff" },
                      { k: "accentColor", label: "Акцент (hover)", hint: "Колір ссылок при наведенні", placeholder: "#ffb100" },
                      { k: "panelColor",  label: "Фон панелі меню", hint: "Блок зі списком підкатегорій (kfx-panel). Порожньо → авто", placeholder: "#fafafb" },
                      { k: "tabsColor",   label: "Фон вкладок",    hint: "Смужка перемикача вкладок (kf-tabs). Порожньо → авто", placeholder: "#f0f0f5" },
                      { k: "mutedColor",  label: "Другорядний текст", hint: "Сірий текст: міста, примітки, підзаголовки, неактивні вкладки. Порожньо → авто", placeholder: "#6b6b78" },
                      { k: "lineColor",   label: "Рамки/лінії",    hint: "Кольори бордерів та розділювачів. Порожньо → авто", placeholder: "#e6e6ec" },
                      { k: "cardColor",   label: "Фон карток/наведення", hint: "Фон карток та підсвітка при наведенні на посилання. Порожньо → авто", placeholder: "#ffffff" },
                      { k: "softColor",   label: "Активна категорія", hint: "Фон обраної категорії/чіпа. Порожньо → авто (відтінок акценту)", placeholder: "#eef0ff" },
                    ].map(({ k, label, hint, placeholder }) => {
                      const val = footerCfg[k] || "";
                      return (
                        <div key={k}>
                          <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</label>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input type="color" value={val || (placeholder)}
                              onChange={(e) => setFooterCfg(prev => ({ ...prev, [k]: e.target.value }))}
                              onBlur={() => saveFooterCfg(footerCfg)}
                              style={{ width: 36, height: 32, padding: 0, border: `1px solid ${t.border}`, borderRadius: 6, cursor: "pointer", background: "transparent" }} />
                            <input type="text" value={val}
                              onChange={(e) => setFooterCfg(prev => ({ ...prev, [k]: e.target.value }))}
                              onBlur={() => saveFooterCfg(footerCfg)}
                              placeholder={placeholder + " (авто)"}
                              style={{ ...inp, fontFamily: "monospace", fontSize: 11, padding: "6px 10px", flex: 1, minWidth: 0 }} />
                            {val && (
                              <button onClick={() => { const next = { ...footerCfg, [k]: "" }; setFooterCfg(next); saveFooterCfg(next); }}
                                title="Скинути на авто"
                                style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 14, padding: "0 4px" }}>×</button>
                            )}
                          </div>
                          <div style={{ fontSize: 9.5, color: t.textMuted, marginTop: 3, lineHeight: 1.3 }}>{hint}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top menu — pick from site structure */}
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>Топ меню — обраних: {(footerCfg.menu || []).length} / {structurePages.length}</div>
                      <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 2 }}>
                        Виберіть сторінки зі структури сайту. Анкор за замовчуванням = назва сторінки. Можна перейменувати — буде показано в футері.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={applyHfToMenu} disabled={!(footerCfg.menu || []).length || footerSaving}
                        title="Замінити назви обраних пунктів на ВЧ-ключ (hf_keyword) з кластерів за збігом URL"
                        style={{ ...btnSm, fontSize: 11, opacity: (footerCfg.menu || []).length ? 1 : 0.5 }}>🔑 ВЧ-ключі в анкори</button>
                      <button onClick={() => {
                        const next = { ...footerCfg, menu: structurePages.map(p => ({ url: p.url, anchor: p.name })) };
                        saveFooterCfg(next);
                      }} style={{ ...btnSm, fontSize: 11, color: "#6366f1", borderColor: "#6366f133", fontWeight: 700 }}>☑ Обрати всі ({structurePages.length})</button>
                      <button onClick={() => saveFooterCfg({ ...footerCfg, menu: [] })} style={{ ...btnSm, fontSize: 11 }}>Очистити</button>
                      <button onClick={exportMenuCsv} disabled={!(footerCfg.menu || []).length}
                        title="Вивантажити сформоване топ-меню (обрані пункти + анкори/ключі) у CSV-таблицю"
                        style={{ ...btnSm, fontSize: 11, color: "#10b981", borderColor: "#10b98133", opacity: (footerCfg.menu || []).length ? 1 : 0.5 }}>⬇ Таблиця</button>
                    </div>
                  </div>

                  {structurePages.length === 0 ? (
                    <div style={{ padding: "20px 12px", textAlign: "center", fontSize: 12, color: t.textMuted, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 8 }}>
                      Структура сайту не заповнена. Зайдіть в модуль «Структура сайту» та згенеруйте.
                    </div>
                  ) : (
                    <>
                      <input type="text" value={menuFilter} onChange={e => setMenuFilter(e.target.value)}
                        placeholder="🔍 Фільтр за назвою або URL..."
                        style={{ ...inp, fontSize: 12, padding: "7px 12px", width: "100%", marginBottom: 8 }} />

                      <div style={{ maxHeight: 360, overflowY: "auto", border: `1px solid ${t.border}`, borderRadius: 8, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
                        {(() => {
                          const menuMap = new Map((footerCfg.menu || []).map(m => [m.url, m.anchor]));
                          const filter = menuFilter.toLowerCase().trim();
                          const visible = filter
                            ? structurePages.filter(p => p.name.toLowerCase().includes(filter) || p.url.toLowerCase().includes(filter))
                            : structurePages;
                          if (visible.length === 0) {
                            return <div style={{ padding: "16px", textAlign: "center", fontSize: 12, color: t.textMuted }}>Нічого не знайдено</div>;
                          }
                          return visible.map((p, i) => {
                            const checked = menuMap.has(p.url);
                            const customAnchor = checked ? menuMap.get(p.url) : p.name;
                            return (
                              <div key={p.url + i} style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "7px 12px", paddingLeft: 12 + p.depth * 16,
                                borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"}`,
                                background: checked ? (dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)") : "transparent",
                              }}>
                                <input type="checkbox" checked={checked} onChange={(e) => {
                                  const next = { ...footerCfg };
                                  if (e.target.checked) {
                                    next.menu = [...(next.menu || []), { url: p.url, anchor: p.name }];
                                  } else {
                                    next.menu = (next.menu || []).filter(m => m.url !== p.url);
                                  }
                                  saveFooterCfg(next);
                                }} style={{ flexShrink: 0, cursor: "pointer" }} />
                                {checked ? (
                                  <input type="text" value={customAnchor}
                                    onChange={e => {
                                      const newAnchor = e.target.value;
                                      const next = { ...footerCfg, menu: (footerCfg.menu || []).map(m => m.url === p.url ? { ...m, anchor: newAnchor } : m) };
                                      setFooterCfg(next);
                                    }}
                                    onBlur={() => saveFooterCfg(footerCfg)}
                                    style={{ ...inp, fontSize: 12, padding: "4px 8px", flex: 1, minWidth: 0 }} />
                                ) : (
                                  <span style={{ fontSize: 12.5, color: t.text, flex: 1, fontWeight: 500 }}>{p.name}</span>
                                )}
                                <span style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }} title={p.url}>
                                  {p.url.replace(/^https?:\/\/[^/]+/, "")}
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  )}

                  {catRoots.length >= 2 && (() => {
                    const menuMap = new Map((footerCfg.menu || []).map(m => [m.url, m.anchor]));
                    const anchorForCat = (r) => cap(menuMap.get(r.url) || r.name || "");
                    return (
                      <div style={{ marginTop: 12, borderTop: `1px dashed ${t.border}`, paddingTop: 10 }}>
                        <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 2 }}>Порядок категорій у футері</div>
                        <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 8 }}>Перетягніть рядок або стрілками ↑↓ змініть порядок категорій верхнього рівня. Підкатегорії не чіпаються.</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {catRoots.map((r, i) => (
                            <div key={r.url}
                              draggable
                              onDragStart={() => setDragCatUrl(r.url)}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => { e.preventDefault(); moveCat(dragCatUrl, r.url); setDragCatUrl(null); }}
                              onDragEnd={() => setDragCatUrl(null)}
                              style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: dragCatUrl === r.url ? (dark ? "rgba(99,102,241,0.14)" : "rgba(99,102,241,0.08)") : (dark ? "rgba(255,255,255,0.02)" : "#fff"), cursor: "grab" }}>
                              <span style={{ color: t.textMuted, fontSize: 14, cursor: "grab" }} title="Перетягнути">⠿</span>
                              <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{anchorForCat(r)}</span>
                              <span style={{ fontSize: 10, color: t.textMuted, minWidth: 16, textAlign: "center" }}>{i + 1}</span>
                              <button onClick={() => moveCatBy(r.url, -1)} disabled={i === 0} style={{ ...btnSm, fontSize: 12, padding: "2px 7px", opacity: i === 0 ? 0.35 : 1 }}>↑</button>
                              <button onClick={() => moveCatBy(r.url, 1)} disabled={i === catRoots.length - 1} style={{ ...btnSm, fontSize: 12, padding: "2px 7px", opacity: i === catRoots.length - 1 ? 0.35 : 1 }}>↓</button>
                            </div>
                          ))}
                        </div>
                        {(footerCfg.catOrder || []).length > 0 && (
                          <button onClick={() => saveFooterCfg({ ...footerCfg, catOrder: [] })} style={{ ...btnSm, fontSize: 10.5, marginTop: 8 }}>↺ Скинути порядок (за структурою)</button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Top queries — regenerate */}
                <div style={{ padding: "12px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>Топ запити — 24 унікальних анкора на кожну сторінку</div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                        Згенеровано для: <strong style={{ color: t.text }}>{footerCfg.queries?.pagesGenerated || 0}</strong> сторінок
                        {footerCfg.queries?.lastGeneratedAt && <span> · {new Date(footerCfg.queries.lastGeneratedAt).toLocaleString("uk-UA")}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => regenFooter(false)} disabled={footerRegen} style={{ ...btnP, fontSize: 12, opacity: footerRegen ? 0.5 : 1 }}>
                        {footerRegen ? "Генерація..." : "🎲 Згенерувати нові сторінки"}
                      </button>
                      <button onClick={() => { if (confirm("Перегенерувати ВСІ сторінки заново? Анкори зміняться.")) regenFooter(true); }} disabled={footerRegen}
                        style={{ ...btnG, fontSize: 12, opacity: footerRegen ? 0.5 : 1 }}>
                        ↻ Force re-gen
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 6, lineHeight: 1.5 }}>
                    Анкори беруться з кластерів (приоритет) + GSC top queries (дозаповнення). Розподіл взвешений: ключі з більшою частотою з'являються частіше. Один раз згенерували — анкори зафіксовані per-page і не змінюються (стабільно для індексації Google).
                  </div>

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                    <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em" }}>Міста (через кому) — колонка поряд із запитами</label>
                    <input type="text" value={(footerCfg.cities || []).join(", ")}
                      onChange={e => { const arr = e.target.value.split(/[,\n]/).map(s => s.trim()).filter(Boolean); setFooterCfg(prev => ({ ...prev, cities: arr })); }}
                      onBlur={() => saveFooterCfg(footerCfg)}
                      placeholder="Київ, Харків, Одеса, Дніпро, Львів"
                      style={{ ...inp, fontSize: 12, padding: "8px 12px", width: "100%" }} />
                    <div style={{ fontSize: 9.5, color: t.textMuted, marginTop: 4 }}>Показуються як текст (без посилань) поряд із кожною групою запитів — локальний сигнал.</div>
                  </div>

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>Перегляд і чистка анкорів{queriesList ? ` · ${queriesList.length}` : ""}</div>
                      <button onClick={loadQueries} disabled={queriesLoading} style={{ ...btnG, fontSize: 11, opacity: queriesLoading ? 0.5 : 1 }}>
                        {queriesLoading ? "Завантаження..." : (queriesList ? "↻ Оновити" : "👁 Показати всі запити")}
                      </button>
                    </div>
                    <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 4 }}>Прогорніть усі запити з їхніми сторінками та видаліть сміттєві (×). Видалення прибирає анкор з УСІХ сторінок.</div>
                    {queriesList && (
                      <>
                        <input type="text" value={qFilter} onChange={e => setQFilter(e.target.value)} placeholder="Пошук анкора або сторінки..."
                          style={{ ...inp, fontSize: 12, padding: "7px 11px", width: "100%", marginTop: 8, boxSizing: "border-box" }} />
                        <div style={{ maxHeight: 340, overflowY: "auto", marginTop: 8, border: `1px solid ${dark ? "#1e1e2c" : "#eee"}`, borderRadius: 8 }}>
                          {queriesList.filter(it => { const q = qFilter.toLowerCase(); return !q || it.anchor.toLowerCase().includes(q) || (it.url || "").toLowerCase().includes(q); }).map(it => (
                            <div key={it.anchor} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: `1px solid ${dark ? "#16161f" : "#f4f4f4"}` }}>
                              <span style={{ flex: 1, fontSize: 12, color: t.text, wordBreak: "break-word" }}>{it.anchor}</span>
                              <span style={{ fontSize: 10.5, color: t.textMuted, fontFamily: "monospace", maxWidth: "42%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.url}>{"\u2192 "}{(() => { try { return new URL(it.url).pathname; } catch { return it.url; } })()}</span>
                              <span style={{ fontSize: 9.5, color: t.textMuted }}>{"\u00d7"}{it.pages}</span>
                              <button onClick={() => removeQuery(it.anchor)} title="Видалити з усіх сторінок"
                                style={{ background: "transparent", border: `1px solid ${dark ? "#333" : "#ddd"}`, color: "#ef4444", borderRadius: 6, cursor: "pointer", padding: "2px 9px", fontSize: 13, lineHeight: 1, fontWeight: 700 }}>{"\u00d7"}</button>
                            </div>
                          ))}
                          {queriesList.filter(it => { const q = qFilter.toLowerCase(); return !q || it.anchor.toLowerCase().includes(q) || (it.url || "").toLowerCase().includes(q); }).length === 0 && (
                            <div style={{ padding: 12, fontSize: 12, color: t.textMuted, textAlign: "center" }}>Порожньо</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Top cards — collect by [data-spalah-card] marker */}
                <div style={{ padding: "12px 18px", borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>Топ-картки — збір зі сайту</div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                        Зібрано: <strong style={{ color: t.text }}>{cards.items?.length || 0}</strong> карток
                        {cards.collectedAt && <span> · {new Date(cards.collectedAt).toLocaleString("uk-UA")} · сторінок: {cards.pagesCrawled || 0}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button onClick={collectCards} disabled={collectingCards} style={{ ...btnP, fontSize: 12, opacity: collectingCards ? 0.5 : 1 }}>
                        {collectingCards ? "Збираю..." : "🃏 Зібрати картки з сайту"}
                      </button>
                      <label title="Файл CSV/TSV/TXT: назва + посилання на картку товара в рядку. Формує блок «Топ картки» без краулу." style={{ ...btnG, fontSize: 12, cursor: importingCards ? "default" : "pointer", opacity: importingCards ? 0.5 : 1, display: "inline-flex", alignItems: "center" }}>
                        {importingCards ? "Завантажую..." : "📄 Завантажити з файлу"}
                        <input type="file" accept=".csv,.tsv,.txt" onChange={importCards} disabled={importingCards} style={{ display: "none" }} />
                      </label>
                      <button onClick={rewriteCards} disabled={rewritingCards || !(cards.items?.length)}
                        title="Прогнати назви карток через OpenAI: на початку назви стане частотний ключ сторінки (матч по URL до семантики). Оригінали зберігаються."
                        style={{ ...btnG, fontSize: 12, opacity: (rewritingCards || !(cards.items?.length)) ? 0.5 : 1 }}>
                        {rewritingCards ? "✨ Переписую..." : "✨ Ключ у назву (OpenAI)"}
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                    Авто-визначити картки на довільній CMS неможливо. Тому розробник сайту додає маркер <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>data-spalah-card</code> на кожну картку — кнопка вище краулить sitemap і збирає H1 + посилання. (SSR/звичайна CMS — ок; для SPA де картки малюються JS — серверний фетч може не побачити.)
                  </div>
                  {(cards.items?.length > 0) && (() => {
                    const q = cardSearch.trim().toLowerCase();
                    const all = cards.items || [];
                    const filtered = q ? all.filter(c => (c.title || "").toLowerCase().includes(q) || (c.url || "").toLowerCase().includes(q)) : all;
                    const PER = 150;
                    const pageCount = Math.max(1, Math.ceil(filtered.length / PER));
                    const pg = Math.min(cardPage, pageCount - 1);
                    const shown = filtered.slice(pg * PER, pg * PER + PER);
                    const editCount = Object.keys(cardEdits).length;
                    return (
                      <div style={{ marginTop: 14, border: `1px solid ${t.border}`, borderRadius: 10, overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: dark ? "#0f0f18" : "#f8f9fb", flexWrap: "wrap" }}>
                          <div style={{ fontSize: 12, fontWeight: 700 }}>Редагувати назви карток (H1)</div>
                          <input value={cardSearch} onChange={e => { setCardSearch(e.target.value); setCardPage(0); }} placeholder="🔍 фільтр за назвою або URL…"
                            style={{ ...inp, fontSize: 11.5, padding: "5px 10px", flex: 1, minWidth: 160 }} />
                          <span style={{ fontSize: 10.5, color: t.textMuted }}>{filtered.length} карток{editCount ? ` · змін: ${editCount}` : ""}</span>
                          {pageCount > 1 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <button onClick={() => setCardPage(Math.max(0, pg - 1))} disabled={pg === 0} style={{ ...btnSm, fontSize: 12, opacity: pg === 0 ? 0.4 : 1 }}>←</button>
                              <span style={{ fontSize: 10.5, color: t.textMuted, minWidth: 54, textAlign: "center" }}>стор {pg + 1}/{pageCount}</span>
                              <button onClick={() => setCardPage(Math.min(pageCount - 1, pg + 1))} disabled={pg >= pageCount - 1} style={{ ...btnSm, fontSize: 12, opacity: pg >= pageCount - 1 ? 0.4 : 1 }}>→</button>
                            </span>
                          )}
                          <button onClick={saveCards} disabled={savingCards || !editCount} style={{ ...btnP, fontSize: 11.5, opacity: (savingCards || !editCount) ? 0.5 : 1 }}>
                            {savingCards ? "Зберігаю…" : `💾 Зберегти${editCount ? ` (${editCount})` : ""}`}
                          </button>
                        </div>
                        <div style={{ maxHeight: 420, overflowY: "auto" }}>
                          {shown.map((card, i) => {
                            const cur = cardEdits[card.url] != null ? cardEdits[card.url] : (card.title || "");
                            const changed = cardEdits[card.url] != null && cardEdits[card.url] !== card.title;
                            return (
                              <div key={card.url + i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderTop: `1px solid ${t.border}`, background: changed ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)") : "transparent" }}>
                                <input value={cur} onChange={e => setCardEdits(prev => ({ ...prev, [card.url]: e.target.value }))}
                                  style={{ ...inp, fontSize: 12, padding: "5px 9px", flex: 1 }} />
                                <a href={card.url} target="_blank" rel="noopener noreferrer" title={card.url} style={{ fontSize: 10.5, color: "#6366f1", textDecoration: "none", whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>↗ {(card.url || "").replace(/^https?:\/\/[^/]+/, "") || "/"}</a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: 8, position: "relative" }}>
                    <pre style={{ fontSize: 11, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word", color: t.text, margin: 0, padding: "12px 14px", borderRadius: 8, background: dark ? "#0a0a10" : "#f8f9fb", border: `1px solid ${t.border}` }}>{CARD_SNIPPET}</pre>
                    <button onClick={() => { navigator.clipboard.writeText(CARD_SNIPPET); flash("Скопійовано"); }} style={{ ...btnSm, fontSize: 10, position: "absolute", top: 8, right: 8 }}>Копіювати</button>
                  </div>
                </div>

                {/* Top filters — CSV import/export */}
                <div style={{ padding: "12px 18px", borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>Топ-фільтри — імпорт/експорт CSV</div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                        {footerFilters.source === "crawler"
                          ? <><span style={{ color: "#7ac142", fontWeight: 600 }}>● з краулера</span> (дефолт) · <strong style={{ color: t.text }}>{footerFilters.items?.length || 0}</strong> фільтрів, глобально</>
                          : footerFilters.source === "import"
                            ? <><span style={{ color: "#6366f1", fontWeight: 600 }}>● імпортовано вручну</span> · <strong style={{ color: t.text }}>{footerFilters.items?.length || 0}</strong> значень{(() => { const p = new Set((footerFilters.items || []).filter(i => i.url).map(i => i.url)); const g = (footerFilters.items || []).filter(i => !i.url).length; return <>{g ? <span> · глобальних: {g}</span> : null}{p.size ? <span> · сторінок: {p.size}</span> : null}</>; })()}</>
                            : <span>Фільтрів ще немає — запустіть краулер або імпортуйте CSV.</span>}
                        {footerFilters.importedAt && <span> · {new Date(footerFilters.importedAt).toLocaleString("uk-UA")}</span>}
                        {footerFilters.collectedAt && <span> · {new Date(footerFilters.collectedAt).toLocaleString("uk-UA")}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button onClick={exportFiltersCsv} style={{ ...btnG, fontSize: 12 }}>⬇ Експорт CSV-шаблон</button>
                      <label style={{ ...btnP, fontSize: 12, cursor: importingFilters ? "default" : "pointer", opacity: importingFilters ? 0.5 : 1 }}>
                        {importingFilters ? "Імпортую..." : "⬆ Імпорт CSV"}
                        <input type="file" accept=".csv,text/csv" disabled={importingFilters} style={{ display: "none" }}
                          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; importFiltersCsv(f); }} />
                      </label>
                    </div>
                  </div>
                  <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                    За замовчуванням підтягуються фільтри з <strong>краулера</strong> (розмітка <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>data-spalah-filter</code> / <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>SpalahFilters</code>) — показуються глобально на всіх сторінках. Щоб змінити — експортуйте CSV (колонки <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>url,group,value,link</code>), відредагуйте й імпортуйте: тоді показується <strong>ваш</strong> набір. <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>url</code> порожній → фільтр глобальний; заповнений → лише на цій сторінці; <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>group</code> → назва блоку фільтрів (порожньо → плоскі чіпи). Повторний імпорт замінює попередній.
                  </div>
                  <div style={{ marginTop: 8, position: "relative" }}>
                    <pre style={{ fontSize: 11, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word", color: t.text, margin: 0, padding: "12px 14px", borderRadius: 8, background: dark ? "#0a0a10" : "#f8f9fb", border: `1px solid ${t.border}` }}>{FILTERS_CSV_SNIPPET}</pre>
                    <button onClick={() => { navigator.clipboard.writeText(FILTERS_CSV_SNIPPET); flash("Скопійовано"); }} style={{ ...btnSm, fontSize: 10, position: "absolute", top: 8, right: 8 }}>Копіювати</button>
                  </div>
                </div>

                {/* Live preview — new design, tabs */}
                <div style={{ padding: "12px 18px", borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Попередній перегляд — вкладки «Топ-меню» / «Топ-запити»</div>
                  <div style={{ fontSize: 10.5, color: t.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                    Новий дизайн. «Топ-меню»: ієрархія зі структури, лише гілки з обраними пунктами. «Топ-запити»: приклад груп запитів із кластерів + колонка міст. Кольори застосовуються наживо.
                  </div>
                  <FooterMenuPreview cfg={footerCfg} pages={structurePages} clusters={clusters} cards={cards.items || []} filters={footerFilters.items || []} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────── Live preview: new footer design, tab «Топ-меню» ─────────── */

const KF_TABS = ["Топ-меню", "Топ-запити", "Топ-картки", "Топ-фільтри", "LSI-терміни", "FAQ", "Стаття на тему"];

// Маркер для розробника сайту клієнта — на кожну картку товару/категорії.
const CARD_SNIPPET = `<!-- Spalah: маркер картки (на кожну картку товару/категорії) -->
<a href="/smartfony/apple-iphone-15-pro"
   data-spalah-card
   data-spalah-note="312 товарів · від 38 900 ₴"
   data-spalah-cat="Смартфони">
   Смартфони Apple iPhone 15 Pro
</a>

<!-- Мінімум: достатньо data-spalah-card + href + текст усередині.
     data-spalah-note і data-spalah-cat — необов'язкові.
     Якщо маркер на <div>-обгортці — посилання береться з вкладеного <a href>. -->`;

// Приклад CSV для табу «Топ-фільтри»: url,group,value,link.
// url порожній → глобально (на всіх сторінках); заповнений → лише на цій сторінці. link порожній → = url.
const FILTERS_CSV_SNIPPET = `url,group,value,link
,,Tramontina,/nozhi?brand=tramontina
/nozhi,Виробник,Fiskars,/nozhi?brand=fiskars
/nozhi,Ціна,до 500 грн,/nozhi?price=0-500
/nozhi,Матеріал,Нержавіюча сталь,/nozhi?material=steel`;

function hexToRgba(hex, a) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// Rebuild the site-structure tree from the flat pre-order {url,name,depth} list,
// then prune to branches that contain at least one selected page (kept node = selected OR has kept descendant).
// Ручний порядок топ-категорій: перелічені у catOrder — спочатку в заданому порядку, решта — у вихідному.
function orderRoots(roots, catOrder) {
  if (!Array.isArray(catOrder) || !catOrder.length) return roots;
  const idx = new Map(catOrder.map((u, i) => [u, i]));
  return [...roots].sort((a, b) => (idx.has(a.url) ? idx.get(a.url) : 1e9) - (idx.has(b.url) ? idx.get(b.url) : 1e9));
}

function buildPrunedMenuTree(pages, selectedSet) {
  const roots = [];
  const stack = []; // [{ node, depth }]
  for (const p of pages || []) {
    const node = { url: p.url, name: p.name, depth: p.depth, children: [] };
    while (stack.length && stack[stack.length - 1].depth >= p.depth) stack.pop();
    if (stack.length) stack[stack.length - 1].node.children.push(node);
    else roots.push(node);
    stack.push({ node, depth: p.depth });
  }
  const prune = (nodes) => {
    const out = [];
    for (const n of nodes) {
      const kids = prune(n.children);
      if (selectedSet.has(n.url) || kids.length) out.push({ ...n, children: kids });
    }
    return out;
  };
  return prune(roots);
}

// Перша літера — велика (працює і для кирилиці).
const cap = (s) => { s = String(s || ""); return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; };

// Детермінований shuffle (seed по індексу групи) — «рандомно розкидані» міста, але стабільно між ре-рендерами.
function seededShuffle(arr, seed) {
  const a = [...arr];
  let s = (seed >>> 0) || 1;
  const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const PREVIEW_CSS = `
.spkf-prev{--muted:#6b6b78;--line:#e6e6ec;font-family:"Geist",ui-sans-serif,system-ui,-apple-system,sans-serif;color:var(--ink);border-top:1px solid var(--line);padding:28px 28px 18px;-webkit-font-smoothing:antialiased}
.spkf-prev *,.spkf-prev *::before,.spkf-prev *::after{box-sizing:border-box}
.spkf-prev .kf-brand .m{width:38px;height:38px;border-radius:10px;background:linear-gradient(135deg,oklch(0.78 0.22 285),oklch(0.55 0.22 285));display:grid;place-items:center;margin-bottom:12px}
.spkf-prev .kf-brand h4{margin:0 0 6px;font-size:16px;font-weight:700;color:var(--ink)}
.spkf-prev .kf-brand p{margin:0;font-size:12px;line-height:1.5;color:var(--muted);max-width:420px}
.spkf-prev .kf-tabs{display:inline-flex;flex-wrap:wrap;gap:4px;margin-top:14px;padding:4px;background:var(--kf-tabs-bg);border-radius:10px}
.spkf-prev .kf-tab{padding:7px 14px;border-radius:8px;font:600 12.5px/1 inherit;color:var(--muted);user-select:none;cursor:default}
.spkf-prev .kf-tab.active{background:var(--kf-card-bg);color:var(--sp-brand);box-shadow:0 1px 3px rgba(0,0,0,0.08)}
.spkf-prev .kf-pane{margin-top:18px}
.spkf-prev .kfx{display:grid;grid-template-columns:280px 1fr;gap:16px;align-items:start}
.spkf-prev .kfx-cats{display:flex;flex-direction:column;gap:4px}
.spkf-prev .kfx-cat{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;cursor:pointer;border:1px solid transparent;transition:all .14s;user-select:none}
.spkf-prev .kfx-cat:hover{background:var(--kf-panel-bg)}
.spkf-prev .kfx-cat.on{background:var(--sp-brand-soft);border-color:oklch(0.85 0.06 285)}
.spkf-prev .kfx-cat .nm{flex:1;font-size:14px;font-weight:600;color:var(--ink)}
.spkf-prev .kfx-cat .arr{width:18px;height:18px;display:grid;place-items:center;color:#b8b8c4;transition:transform .2s}
.spkf-prev .kfx-cat.on .arr{transform:rotate(90deg);color:var(--sp-brand)}
.spkf-prev .kfx-more{margin-top:8px;padding:9px 12px;border-radius:10px;border:1px dashed var(--sp-brand);background:var(--sp-brand-soft);color:var(--sp-brand);font-size:12.5px;font-weight:600;cursor:pointer;text-align:center;transition:all .14s;user-select:none}
.spkf-prev .kfx-more:hover{background:#fff}
.spkf-prev .kfx-panel{background:var(--kf-panel-bg);border:1px solid var(--line);border-radius:12px;padding:8px;min-height:200px}
.spkf-prev .kfx-sub>summary{display:flex;align-items:center;gap:10px;padding:10px 12px;cursor:pointer;border-radius:9px;transition:background .14s;list-style:none}
.spkf-prev .kfx-sub>summary::-webkit-details-marker{display:none}
.spkf-prev .kfx-sub>summary:hover{background:var(--kf-card-bg)}
.spkf-prev .kfx-sub>summary .nm{flex:1;font-size:13.5px;font-weight:500;color:var(--ink)}
.spkf-prev .kfx-sub>summary .arr{width:16px;height:16px;display:grid;place-items:center;color:#b8b8c4;transition:transform .2s}
.spkf-prev .kfx-sub[open]>summary .arr{transform:rotate(90deg);color:var(--sp-brand)}
.spkf-prev .kfx-sub.leaf>summary{cursor:default}
.spkf-prev .kfx-sub.leaf>summary .arr{opacity:0}
.spkf-prev .kfx-subsub{display:flex;flex-direction:column;gap:2px;padding:2px 0 8px 38px}
.spkf-prev .kfx-subsub a{display:flex;align-items:baseline;gap:8px;font-size:12.5px;color:var(--muted);text-decoration:none;padding:6px 8px;border-radius:7px;transition:all .14s}
.spkf-prev .kfx-subsub a:hover{color:var(--sp-brand);background:var(--kf-card-bg)}
.spkf-prev .kfx-subsub a::before{content:"";width:4px;height:4px;border-radius:50%;background:var(--sp-brand);flex-shrink:0;transform:translateY(-2px);opacity:.5}
.spkf-prev .kf-queries{display:grid;grid-template-columns:repeat(4,1fr);gap:10px 24px}
.spkf-prev .kf-qpair{display:grid;grid-template-columns:1fr auto;gap:14px}
.spkf-prev .kf-qcol a{display:flex;align-items:baseline;gap:8px;font-size:13px;color:var(--ink);text-decoration:none;padding:7px 0;transition:color .14s;line-height:1.4}
.spkf-prev .kf-qcol a:hover{color:var(--sp-brand)}
.spkf-prev .kf-qcol a::before{content:"";width:4px;height:4px;border-radius:50%;background:var(--sp-brand);flex-shrink:0;transform:translateY(-2px);opacity:.5}
.spkf-prev .kf-qcities{display:flex;flex-direction:column;border-left:1px solid var(--line);padding-left:14px}
.spkf-prev .kf-qcities span{font-size:13px;color:var(--muted);padding:7px 0;line-height:1.4;white-space:nowrap}
.spkf-prev .kf-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.spkf-prev .kf-card{display:flex;flex-direction:column;gap:6px;padding:16px 18px;border-radius:12px;background:var(--kf-card-bg);border:1px solid var(--line);transition:all .18s}
.spkf-prev .kf-card:hover{border-color:var(--sp-brand);box-shadow:0 6px 20px oklch(0.58 0.2 285/0.12);transform:translateY(-2px)}
.spkf-prev .kf-card-tag{align-self:flex-start;font:600 9.5px/1 ui-monospace,monospace;letter-spacing:.08em;text-transform:uppercase;color:var(--sp-brand);background:var(--sp-brand-soft);padding:4px 8px;border-radius:5px;text-decoration:none}
.spkf-prev .kf-card-h1{font-size:15px;font-weight:600;color:var(--ink);line-height:1.35;letter-spacing:-.01em;text-decoration:none;transition:color .14s}
.spkf-prev .kf-card-h1:hover{color:var(--sp-brand)}
.spkf-prev .kf-card-note{font-size:11.5px;color:var(--muted)}
.spkf-prev .kf-filters{display:flex;flex-direction:column;gap:18px}
.spkf-prev .kf-fgroup-h{font:700 11px/1 inherit;text-transform:uppercase;letter-spacing:.06em;color:var(--muted);margin-bottom:10px}
.spkf-prev .kf-fchips{display:flex;flex-wrap:wrap;gap:8px}
.spkf-prev .kf-fchip{font-size:12.5px;color:var(--ink);text-decoration:none;padding:6px 13px;border-radius:20px;background:var(--kf-panel-bg);border:1px solid var(--line);transition:all .14s}
.spkf-prev .kf-fchip:hover{color:var(--sp-brand);border-color:var(--sp-brand)}
.spkf-prev .kf-lsi-intro{font-size:12px;color:var(--muted);margin-bottom:14px;max-width:560px;line-height:1.6}
.spkf-prev .kf-lsi{display:flex;flex-wrap:wrap;gap:7px}
.spkf-prev .kf-lsi-tag{display:inline-flex;align-items:center;padding:6px 12px;border-radius:8px;background:var(--kf-panel-bg);border:1px solid var(--line);font-size:12.5px;color:var(--ink);text-decoration:none;transition:all .14s}
.spkf-prev a.kf-lsi-tag:hover{background:var(--sp-brand-soft);border-color:var(--sp-brand);color:var(--sp-brand)}
.spkf-prev .kf-lsi-tag.plain{background:transparent;border-style:dashed;color:var(--muted)}
.spkf-prev .kf-bottom{display:flex;align-items:center;justify-content:space-between;margin-top:24px;padding-top:16px;border-top:1px solid var(--line);font-size:11px;color:var(--muted)}
.spkf-prev .kf-bottom .attr{display:inline-flex;align-items:center;gap:6px}
.spkf-prev .kf-bottom .attr b{color:var(--sp-brand)}
@media(max-width:820px){.spkf-prev .kfx{grid-template-columns:1fr}.spkf-prev .kf-queries{grid-template-columns:1fr 1fr}.spkf-prev .kf-cards{grid-template-columns:1fr 1fr}}
`;

function FooterMenuPreview({ cfg, pages, clusters, cards, filters }) {
  const menu = Array.isArray(cfg.menu) ? cfg.menu : [];
  const cities = Array.isArray(cfg.cities) ? cfg.cities : [];
  const cardList = (Array.isArray(cards) ? cards : []).slice(0, 6);
  const anchorMap = useMemo(() => new Map(menu.map(m => [m.url, m.anchor])), [menu]);
  const selectedSet = useMemo(() => new Set(menu.map(m => m.url)), [menu]);
  const tree = useMemo(() => orderRoots(buildPrunedMenuTree(pages, selectedSet), cfg.catOrder), [pages, selectedSet, cfg.catOrder]);
  // Демо-групи запитів для прев'ю: до 4 кластерів, у кожному до 5 ключів (на клієнті це per-page frozen-анкори).
  const queryGroups = useMemo(() => {
    const kw = (k) => (k && typeof k === "object") ? String(k.keyword || k.kw || k.anchor || "") : String(k || "");
    return (clusters || [])
      .filter(c => c && c.url && ((c.keywords && c.keywords.length) || c.hfKeyword))
      .slice(0, 4)
      .map(c => ({
        url: c.url,
        items: ((c.keywords && c.keywords.length ? c.keywords : [c.hfKeyword]).map(kw).filter(Boolean)).slice(0, 5),
      }))
      .filter(g => g.items.length);
  }, [clusters]);
  // На кожну групу запитів — свій перемішаний набір міст (рандомно розкидані).
  const cityCols = useMemo(
    () => queryGroups.map((_, gi) => seededShuffle(cities, gi * 7 + 1).slice(0, 5)),
    [cities, queryGroups]
  );
  // Прев'ю фільтрів: показуємо групи сторінки з найбільшою к-стю фільтрів (репрезентативна).
  const filterGroups = useMemo(() => {
    const items = Array.isArray(filters) ? filters : [];
    if (!items.length) return [];
    const byUrl = new Map();
    for (const it of items) { if (!it || !it.url || !it.value) continue; const a = byUrl.get(it.url) || []; a.push(it); byUrl.set(it.url, a); }
    let best = null, bestN = 0;
    for (const arr of byUrl.values()) { if (arr.length > bestN) { bestN = arr.length; best = arr; } }
    if (!best) return [];
    const groups = [];
    for (const it of best) { let g = groups.find(x => x.group === (it.group || "")); if (!g) { g = { group: it.group || "", items: [] }; groups.push(g); } g.items.push({ value: it.value, link: it.link || it.url }); }
    return groups.map(g => ({ ...g, items: g.items.slice(0, 40) })); // прев'ю: до 40 чіпів на групу
  }, [filters]);

  // Прев'ю LSI: наживо це n-грами сайту; тут показуємо приклад із ключів кластерів (усі як лінковані).
  const lsiPreview = useMemo(() => {
    const out = []; const seen = new Set();
    for (const c of (clusters || [])) {
      let kws = c.keywords; if (typeof kws === "string") { try { kws = JSON.parse(kws); } catch { kws = []; } }
      const list = Array.isArray(kws) ? kws : [];
      for (const k of list) {
        const term = String((k && (k.keyword || k.kw || k.text)) || k || "").trim();
        const key = term.toLowerCase();
        if (!term || term.length < 4 || seen.has(key)) continue;
        seen.add(key); out.push({ term, url: c.url });
        if (out.length >= 30) break;
      }
      if (out.length >= 30) break;
    }
    return out;
  }, [clusters]);

  const [active, setActive] = useState(0);
  const [tab, setTab] = useState(0);
  const [showAllCats, setShowAllCats] = useState(false);

  const anchorOf = (n) => cap(anchorMap.get(n.url) || n.name);
  const CAT_LIMIT = 10;
  const catsCollapsed = tree.length > CAT_LIMIT && !showAllCats;
  const visibleCats = catsCollapsed ? tree.slice(0, CAT_LIMIT) : tree;
  const activeIdx = Math.min(active, Math.max(0, tree.length - 1));
  const cat = tree[activeIdx] || null;

  const bg = (cfg.bgColor || "").trim() || "#ffffff";
  const ink = (cfg.textColor || "").trim() || "#1a1a22";
  const brand = (cfg.accentColor || "").trim() || "oklch(0.58 0.2 285)";
  const brandSoft = hexToRgba(cfg.accentColor, 0.13) || "oklch(0.95 0.03 285)";
  const panelBg = (cfg.panelColor || "").trim() || "#fafafb";
  const tabsBg = (cfg.tabsColor || "").trim() || "#f0f0f5";
  const mutedC = (cfg.mutedColor || "").trim() || "#6b6b78";
  const lineC = (cfg.lineColor || "").trim() || "#e6e6ec";
  const cardC = (cfg.cardColor || "").trim() || "#ffffff";
  const softC = (cfg.softColor || "").trim() || brandSoft;
  const vars = { "--sp-brand": brand, "--sp-brand-soft": softC, "--ink": ink, "--muted": mutedC, "--line": lineC, "--kf-panel-bg": panelBg, "--kf-tabs-bg": tabsBg, "--kf-card-bg": cardC, background: bg };

  if (!menu.length) {
    return (
      <div style={{ padding: "20px 12px", textAlign: "center", fontSize: 12, color: "#9a9aa8", border: "1px dashed #ccc", borderRadius: 8 }}>
        Оберіть пункти меню вище — і тут зʼявиться прев'ю.
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #e6e6ec", borderRadius: 12, overflow: "hidden" }}>
      <style>{PREVIEW_CSS}</style>
      <div className="spkf-prev" style={vars}>
        <div className="kf-top">
          <div className="kf-brand">
            <div className="m"><svg width="20" height="20" viewBox="0 0 40 40" fill="none"><path d="M22 6 L11 22 L18 22 L15 34 L29 16 L21 16 L24 6 Z" fill="white" /></svg></div>
            <h4>{(cfg.brandTitle || "").trim() || "Так шукають"}</h4>
            <p>{(cfg.brandSubtitle || "").trim() || "Внутрішня перелінковка від Spalah."}</p>
          </div>
          <div className="kf-tabs">
            {KF_TABS.map((label0, i) => {
              const cfgLabels = [ (cfg.tabMenuLabel||"").trim()||"Топ-меню", (cfg.tabQueriesLabel||"").trim()||"Топ-запити", (cfg.tabCardsLabel||"").trim()||"Топ-картки", (cfg.tabFiltersLabel||"").trim()||"Топ-фільтри", (cfg.tabLsiLabel||"").trim()||"LSI-терміни" ];
              const cfgOn = [ cfg.tabMenu!==false, cfg.tabQueries!==false, cfg.tabCards!==false, cfg.tabFilters!==false, cfg.tabLsi!==false ];
              const label = i <= 4 ? cfgLabels[i] : label0;
              if (i <= 4 && !cfgOn[i]) return null;
              const supported = i <= 4;
              return (
                <span key={label} className={"kf-tab" + (i === tab ? " active" : "")}
                  onClick={() => supported && setTab(i)}
                  title={supported ? "" : "скоро"}
                  style={{ cursor: supported ? "pointer" : "default", opacity: supported ? 1 : 0.5 }}>{label}</span>
              );
            })}
          </div>
        </div>

        <div className="kf-pane">
          {tab === 0 ? (
            !tree.length ? (
            <div style={{ fontSize: 13, color: "#6b6b78", padding: "20px 0" }}>Немає обраних пунктів у структурі сайту.</div>
          ) : (
            <div className="kfx">
              <div className="kfx-cats">
                {visibleCats.map((c, i) => (
                  <label key={c.url + i} className={"kfx-cat" + (i === activeIdx ? " on" : "")} onClick={() => setActive(i)}>
                    <span className="nm">{anchorOf(c)}</span><span className="arr">›</span>
                  </label>
                ))}
                {tree.length > CAT_LIMIT && (
                  <div className="kfx-more" onClick={() => setShowAllCats(v => !v)}>
                    {showAllCats ? "Згорнути" : `Всі категорії (${tree.length})`}
                  </div>
                )}
              </div>
              <div className="kfx-panel">
                {cat && cat.children.length ? cat.children.map((sub, si) => (
                  sub.children.length ? (
                    <details className="kfx-sub" key={sub.url + si} open={si === 0}>
                      <summary><span className="nm">{anchorOf(sub)}</span><span className="arr">›</span></summary>
                      <div className="kfx-subsub">
                        {sub.children.map((leaf, li) => (
                          <a key={leaf.url + li} href={leaf.url} onClick={(e) => e.preventDefault()}>{anchorOf(leaf)}</a>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <details className="kfx-sub leaf" key={sub.url + si}>
                      <summary><span className="nm">{anchorOf(sub)}</span><span className="arr">›</span></summary>
                    </details>
                  )
                )) : (
                  <div style={{ fontSize: 13, color: "#6b6b78", padding: "8px 12px" }}>{cat ? anchorOf(cat) : ""} — без підпунктів у цій гілці.</div>
                )}
              </div>
            </div>
          )
          ) : tab === 1 ? (
            !queryGroups.length ? (
              <div style={{ fontSize: 13, color: "#6b6b78", padding: "20px 0" }}>Немає кластерів — додайте семантику, щоб побачити приклад запитів.</div>
            ) : (
              <div className="kf-queries">
                {queryGroups.map((g, gi) => (
                  <div className="kf-qpair" key={gi}>
                    <div className="kf-qcol">{g.items.map((q, qi) => (<a key={qi} href={g.url} onClick={(e) => e.preventDefault()}>{q}</a>))}</div>
                    {cities.length > 0 && (<div className="kf-qcities">{(cityCols[gi] || []).map((c, ci) => (<span key={ci}>{c}</span>))}</div>)}
                  </div>
                ))}
              </div>
            )
          ) : tab === 2 ? (
            !cardList.length ? (
              <div style={{ fontSize: 13, color: "#6b6b78", padding: "20px 0" }}>Карток ще не зібрано — натисніть «Зібрати картки з сайту».</div>
            ) : (
              <div className="kf-cards">
                {cardList.map((c, ci) => (
                  <div className="kf-card" key={ci}>
                    {c.cat ? <a className="kf-card-tag" href={c.url} onClick={(e) => e.preventDefault()}>{cap(c.cat)}</a> : null}
                    <a className="kf-card-h1" href={c.url} onClick={(e) => e.preventDefault()}>{cap(c.title)}</a>
                    {c.note ? <span className="kf-card-note">{c.note}</span> : null}
                  </div>
                ))}
              </div>
            )
          ) : tab === 3 ? (
            !filterGroups.length ? (
              <div style={{ fontSize: 13, color: "#6b6b78", padding: "20px 0" }}>Фільтрів ще не імпортовано — завантажте CSV-шаблон, заповніть і імпортуйте.</div>
            ) : (
              <div className="kf-filters">
                {filterGroups.map((g, gi) => (
                  <div className="kf-fgroup" key={gi}>
                    {g.group ? <div className="kf-fgroup-h">{cap(g.group)}</div> : null}
                    <div className="kf-fchips">{g.items.map((it, ii) => (<a className="kf-fchip" key={ii} href={it.link} onClick={(e) => e.preventDefault()}>{it.value}</a>))}</div>
                  </div>
                ))}
              </div>
            )
          ) : tab === 4 ? (
            !lsiPreview.length ? (
              <div style={{ fontSize: 13, color: "#6b6b78", padding: "20px 0" }}>Нема n-грам/ключів — LSI наживо будується з n-грам аналізу сайту.</div>
            ) : (
              <div className="kf-lsi">
                {lsiPreview.map((it, ii) => (<a className="kf-lsi-tag" key={ii} href={it.url} onClick={(e) => e.preventDefault()}>{it.term}</a>))}
              </div>
            )
          ) : null}
        </div>

        <div className="kf-bottom">
          <span>© 2026 · Усі права захищені</span>
          {!cfg.hideAttribution && (
          <span className="attr">
            <svg width="12" height="12" viewBox="0 0 40 40" fill="none"><path d="M22 6 L11 22 L18 22 L15 34 L29 16 L21 16 L24 6 Z" fill="currentColor" /></svg>
            перелінковка <b>Spalah</b> від Svitlo agency
          </span>
          )}
        </div>
      </div>
    </div>
  );
}
